import { useSort } from "~/pages/inventory/hooks/useSort";
import {
  FindingsFilter,
  FindingsOrder,
  FindingsOrderField,
  OrderDirection,
  useGetAffectedAssetsQuery,
} from "~/operations";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { ASSET_TABLE_INITIAL_PAGE_RANGE } from "~/components/pagination";
import { usePaginate } from "~/components/pagination/usePaginate";
import {
  ExposedAssetsAdvisoryNode,
  ExposedAssetsCVENode,
  SelectionProps,
} from "~/pages/space/vulnerabilities/components/exposed-assets";
import { useEffect } from "react";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useAssetVulnerabilitiesFilterContext } from "~/pages/inventory/asset/useAssetVulnerabilitiesFilterContext";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import keyBy from "lodash-es/keyBy";
import { ExposedAsset, SelectedAssets } from "../types";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";

type UseAffectedAssetsParams = {
  scope: SpaceOrWorkspaceScope;
  filter: FindingsFilter;
  selectionProps?: SelectionProps | undefined;
  onCreateTicketClick?: (params: {
    selectedAssets: SelectedAssets;
    isScopeLevelTicket: boolean;
  }) => void;
  onCreatePullRequestClick?: (params: {
    selectedAssets: SelectedAssets;
    findings: Array<ExposedAssetsCVENode | ExposedAssetsAdvisoryNode>;
  }) => void;
  onSetExceptionClick?: (params: { selectedAssets: SelectedAssets }) => void;
  onRemoveExceptionClick?: (params: { selectedAssets: SelectedAssets }) => void;
};

export function useExposedAssets({
  scope,
  filter,
  selectionProps,
  onCreateTicketClick,
  onCreatePullRequestClick,
  onSetExceptionClick,
  onRemoveExceptionClick,
}: UseAffectedAssetsParams) {
  const paginate = usePaginate({
    defaultPageSize: ASSET_TABLE_INITIAL_PAGE_RANGE.to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);
  const triageToolbar = useTriageToolbar();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [
      FindingsOrderField.AssetName,
      FindingsOrderField.LastUpdated,
      FindingsOrderField.RiskValue,
      FindingsOrderField.RiskFactors,
    ],
  });

  const { filterForm, filterVariables, activeFilterCount, resetFilters } =
    useAssetVulnerabilitiesFilterContext({
      scopeMrn: scope.mrn,
      paginate,
      baseQueryFilters: filter,
    });

  const { data, error, loading, fetchMore } = useGetAffectedAssetsQuery({
    variables: {
      scopeMrn: scope.mrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        ...filter,
        ...filterVariables,
      },
    },
  });

  const findingsUnion = data?.findings;
  const findings =
    findingsUnion?.__typename === "FindingsConnection"
      ? findingsUnion
      : undefined;
  const cveOrAdvisoryfindingsByMrn: Record<
    string,
    ExposedAssetsAdvisoryNode | ExposedAssetsCVENode | undefined
  > = keyBy(
    findings?.edges
      ?.map(({ node }) => node)
      .filter(
        (item): item is ExposedAssetsAdvisoryNode | ExposedAssetsCVENode =>
          item?.__typename === "CveFinding" ||
          item?.__typename === "AdvisoryFinding",
      ),
    "mrn",
  );

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectionProps?.selectedEntities || [],
    target: "finding",
    showExceptionButtons: true,
    showScopeButtons: false,
  });

  const assets = findings?.edges?.flatMap(({ node }) => {
    if (!node || node.__typename === "GenericFinding") return [];

    const asset: ExposedAsset = {
      isAffectedAsset: true,
      id: node.asset?.id || "",
      mrn: node.mrn || "",
      assetMrn: node.asset?.mrn || "",
      score: node.riskValue,
      lastUpdated: node.lastUpdated,
      riskFactors: node.riskFactors,
      title: node.asset?.name || "",
      iconId: node.asset?.icon,
      tags: node.asset?.tags,
      rating: node.rating,
      ...(node.__typename !== "PackageFinding" && {
        cases: node.cases,
        exception: node.exception,
        state: node.state,
        spaceRiskDelta: node.spaceRiskDelta,
      }),
      __typename: node.__typename,
    };

    return [asset];
  });

  useEffect(() => {
    if ((selectionProps?.selectedEntities || []).length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.CreateTicket,
            onClick: () => {
              onCreateTicketClick?.({
                selectedAssets: selectionProps?.selectedEntities || [],
                isScopeLevelTicket: false,
              });
            },
            disabled: false,
            tooltip: "",
          },
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: () => {
                    onSetExceptionClick?.({
                      selectedAssets: selectionProps?.selectedEntities || [],
                    });
                  },
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: () => {
                    onRemoveExceptionClick?.({
                      selectedAssets: selectionProps?.selectedEntities || [],
                    });
                  },
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          ...(hasSecurityPipelineIntegrations
            ? [
                {
                  type: TriageToolbarButtonType.CreateRemediationWorkflow,
                  onClick: () => {
                    const selectedAssets =
                      selectionProps?.selectedEntities ?? [];
                    const findings = selectedAssets.flatMap(
                      (entity) => cveOrAdvisoryfindingsByMrn[entity.mrn] ?? [],
                    );
                    onCreatePullRequestClick?.({
                      selectedAssets,
                      findings,
                    });
                  },
                  disabled: false,
                  tooltip: "",
                },
              ]
            : []),
        ],
        payload: {
          count: selectionProps?.selectedEntities?.length || 0,
          totalCount: findings?.totalCount || 0,
          onClose: selectionProps?.onCancel,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectionProps?.selectedEntities]);

  return {
    assets,
    sort,
    paginate,
    fetchMore,
    // Backend is currently returning a 0 for filteredTotalCount, so we're using totalCount for now
    // but this will not be accurate when filtering is implemented
    filteredTotalCount: findings?.totalCount || 0,
    hasAffectedAssets: (assets?.length || 0) > 0,
    activeFilterCount,
    filterForm,
    resetFilters,
    pageInfo: findings?.pageInfo,
    error,
    loading,
  };
}
